/* eslint-disable react/no-unknown-property -- project-standard Cypress selectors */
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import StatusBadge from "../ui/status_badge";
import { statusMetadata } from "../ui/status_metadata";
import AppointmentRequestSimpleDisplay, {
  appointmentUnavailableToPeer
} from "./appointment_request_simple_display";
import {
  appointmentClient,
  appointmentClientNumber,
  appointmentDestination,
  appointmentMatchedPeer,
  appointmentPickup,
  appointmentUnit,
  appointmentRecurrence,
  appointmentTime
} from "./appointment_requests_calendar_utils";

const formatAgendaDate = (day) =>
  day.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  });

const appointmentStatus = (appointment) => statusMetadata(appointment.status);

const stableAppointmentKey = (appointment, index) =>
  appointment.occurrence_key || appointment.id || `agenda-appointment-${index}`;

const StatusPill = ({ appointment, userData }) => {
  const status = appointmentStatus(appointment);
  const unavailableToViewer =
    userData.account_type === "Peer" && appointmentUnavailableToPeer(appointment, userData.id);

  return (
    <span className="peer_dashboard_status_group">
      <StatusBadge
        className={`peer_dashboard_status_pill ${status.className}`}
        label={status.label}
        status={appointment.status}
      />
      {unavailableToViewer && (
        <StatusBadge
          className="peer_dashboard_status_pill status-unavailable"
          status="unavailable"
        />
      )}
    </span>
  );
};

StatusPill.propTypes = {
  appointment: PropTypes.object.isRequired,
  userData: PropTypes.object.isRequired
};

const AppointmentActions = ({ appointment, userData }) => (
  <AppointmentRequestSimpleDisplay
    data={appointment}
    userId={userData.id}
    isPeer={userData.account_type === "Peer"}
    accountType={userData.account_type}
    actionsOnly
  />
);

AppointmentActions.propTypes = {
  appointment: PropTypes.object.isRequired,
  userData: PropTypes.object.isRequired
};

const MobileAppointmentCard = ({ appointment, userData }) => {
  const status = appointmentStatus(appointment);
  const recurring = appointment.is_recurring || appointment.recurring;
  return (
    <article className={`ccw-surface peer_dashboard_mobile_agenda_card ${status.className}`}>
      <div className="peer_dashboard_mobile_agenda_heading">
        <div>
          <p>{appointmentTime(appointment)}</p>
          <h3>{appointmentClient(appointment)}</h3>
          <span>Client #{appointmentClientNumber(appointment)}</span>
        </div>
        <StatusPill appointment={appointment} userData={userData} />
      </div>
      <dl className="peer_dashboard_mobile_agenda_details">
        <div>
          <dt>Unit</dt>
          <dd>{appointmentUnit(appointment)}</dd>
        </div>
        <div>
          <dt>Pickup</dt>
          <dd>{appointmentPickup(appointment)}</dd>
        </div>
        <div>
          <dt>Destination</dt>
          <dd>{appointmentDestination(appointment)}</dd>
        </div>
        <div>
          <dt>Matched Peer</dt>
          <dd>{appointmentMatchedPeer(appointment)}</dd>
        </div>
        <div>
          <dt>Recurrence</dt>
          <dd>
            {recurring && <span aria-hidden="true">↻ </span>}
            {appointmentRecurrence(appointment)}
          </dd>
        </div>
      </dl>
      {appointment.issue_with_appointment && (
        <p className="peer_dashboard_agenda_issue">
          <strong>Reason:</strong> {appointment.issue_with_appointment}
        </p>
      )}
      <div className="peer_dashboard_agenda_actions">
        <AppointmentActions appointment={appointment} userData={userData} />
      </div>
    </article>
  );
};

MobileAppointmentCard.propTypes = {
  appointment: PropTypes.object.isRequired,
  userData: PropTypes.object.isRequired
};

const AppointmentRequestsDayAgenda = ({
  day,
  appointments,
  userData,
  isMobile,
  activeFilters,
  onClose
}) => {
  const closeButtonRef = useRef(null);
  const unmatchedCount = appointments.filter(
    (appointment) => appointment.status === "unmatched"
  ).length;
  const recurringCount = appointments.filter(
    (appointment) => appointment.is_recurring || appointment.recurring
  ).length;
  const hasActiveFilters = activeFilters.length > 0;

  useEffect(() => {
    if (!day) return undefined;

    document.body.classList.add("peer-dashboard-agenda-open");
    return () => document.body.classList.remove("peer-dashboard-agenda-open");
  }, [day]);

  return (
    <Dialog
      open={Boolean(day)}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="xl"
      scroll="paper"
      aria-labelledby="peer-dashboard-day-agenda-title"
      aria-describedby="peer-dashboard-day-agenda-summary"
      PaperProps={{ className: "ccw-dialog peer_dashboard_day_agenda_dialog" }}
      TransitionProps={{
        onEntered: () => {
          if (closeButtonRef.current) closeButtonRef.current.focus();
        }
      }}
    >
      <DialogTitle
        component="div"
        className="ccw-dialog__header peer_dashboard_day_agenda_titlebar"
      >
        <div>
          <p className="peer_dashboard_eyebrow">Selected day</p>
          <h2 id="peer-dashboard-day-agenda-title">
            {day ? formatAgendaDate(day) : "Appointments"}
          </h2>
          <p id="peer-dashboard-day-agenda-summary">
            {appointments.length} appointment{appointments.length === 1 ? "" : "s"} ·{" "}
            {unmatchedCount} unmatched
            {recurringCount > 0 ? ` · ${recurringCount} recurring` : ""}
          </p>
        </div>
        <div className="peer_dashboard_day_agenda_header_actions">
          <button
            type="button"
            className="ccw-button ccw-button--quiet peer_dashboard_day_agenda_print"
            explorator_test_id="peer-dashboard-day-agenda-print"
            onClick={() => window.print()}
          >
            <PrintIcon fontSize="small" aria-hidden="true" />
            Print / Save PDF
          </button>
          <IconButton
            ref={closeButtonRef}
            className="ccw-button ccw-button--icon"
            aria-label="Close selected day"
            explorator_test_id="peer-dashboard-day-agenda-close"
            onClick={onClose}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers className="ccw-dialog__body peer_dashboard_day_agenda_content">
        <div
          className="ccw-notice peer_dashboard_day_agenda_notice"
          explorator_test_id="peer-dashboard-day-agenda-filter-summary"
        >
          <div>
            <strong>
              {hasActiveFilters
                ? "Showing filtered appointments"
                : "Showing all appointments for this day."}
            </strong>
            {hasActiveFilters && (
              <ul className="peer_dashboard_day_agenda_filters" aria-label="Active filters">
                {activeFilters.map(({ key, label, displayValue }) => (
                  <li key={key} explorator_test_id={`peer-dashboard-day-agenda-filter-${key}`}>
                    <span>{label}</span>
                    <strong>{displayValue}</strong>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <small>Whole-day requests appear first, followed by timed appointments.</small>
        </div>

        {appointments.length === 0 ? (
          <div className="peer_dashboard_day_agenda_empty">
            <h3>No appointments to show</h3>
            <p>This day is empty, or no appointments match the active filters.</p>
          </div>
        ) : isMobile ? (
          <div className="peer_dashboard_mobile_agenda">
            {appointments.map((appointment, index) => (
              <MobileAppointmentCard
                key={stableAppointmentKey(appointment, index)}
                appointment={appointment}
                userData={userData}
              />
            ))}
          </div>
        ) : (
          <div className="ccw-table-shell peer_dashboard_agenda_table_shell">
            <table className="ccw-table peer_dashboard_agenda_table">
              <caption>
                Appointment details and actions for{" "}
                {day ? formatAgendaDate(day) : "the selected day"}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">Client</th>
                  <th scope="col">Trip</th>
                  <th scope="col">Peer & status</th>
                  <th scope="col">Recurrence</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((appointment, index) => {
                  const recurring = appointment.is_recurring || appointment.recurring;
                  return (
                    <tr key={stableAppointmentKey(appointment, index)}>
                      <td className="peer_dashboard_agenda_time">{appointmentTime(appointment)}</td>
                      <td>
                        <strong>{appointmentClient(appointment)}</strong>
                        <span>Client #{appointmentClientNumber(appointment)}</span>
                      </td>
                      <td>
                        <small>Unit</small>
                        <strong>{appointmentUnit(appointment)}</strong>
                        <small>Pickup</small>
                        <strong>{appointmentPickup(appointment)}</strong>
                        <small>Destination</small>
                        <span>{appointmentDestination(appointment)}</span>
                      </td>
                      <td>
                        <span>{appointmentMatchedPeer(appointment)}</span>
                        <StatusPill appointment={appointment} userData={userData} />
                        {appointment.issue_with_appointment && (
                          <small>{appointment.issue_with_appointment}</small>
                        )}
                      </td>
                      <td>
                        <span>
                          {recurring && <span aria-hidden="true">↻ </span>}
                          {appointmentRecurrence(appointment)}
                        </span>
                        {appointment.expandable_series && <small>Series actions available</small>}
                        {recurring && !appointment.expandable_series && (
                          <small>Saved occurrence</small>
                        )}
                      </td>
                      <td>
                        <div className="peer_dashboard_agenda_actions">
                          <AppointmentActions appointment={appointment} userData={userData} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

AppointmentRequestsDayAgenda.propTypes = {
  day: PropTypes.instanceOf(Date),
  appointments: PropTypes.array,
  userData: PropTypes.object,
  isMobile: PropTypes.bool,
  activeFilters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      displayValue: PropTypes.string.isRequired
    })
  ),
  onClose: PropTypes.func
};

AppointmentRequestsDayAgenda.defaultProps = {
  day: null,
  appointments: [],
  userData: {},
  isMobile: false,
  activeFilters: [],
  onClose: () => {}
};

export default AppointmentRequestsDayAgenda;
